import React, { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import classNames from 'classnames';
import { motion } from 'framer-motion';

import { CheckIcon, CopyContentIcon } from '../../assets';

import styles from './CopyInput.module.scss';

export interface ICopyInputProps extends React.ComponentProps<'input'> {
    showCopyButton?: boolean;
}

export const CopyInput = forwardRef<HTMLInputElement, ICopyInputProps>(
    ({ className, showCopyButton = false, ...props }, ref) => {
        const localRef = useRef<HTMLInputElement>(null);
        const [copied, setCopied] = useState(false);
        const [copyTimeout, setCopyTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);

        useImperativeHandle(ref, () => localRef.current!, []);

        const handleCopy = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            if (!localRef.current) return;

            localRef.current.select();
            localRef.current.setSelectionRange(0, 99999);
            navigator.clipboard.writeText(localRef.current.value);

            setCopied(true);
            copyTimeout && clearTimeout(copyTimeout);
            setCopyTimeout(setTimeout(() => setCopied(false), 2000));
        };

        return (
            <div className={styles.inputWrapper}>
                <input
                    ref={localRef}
                    className={classNames(
                        styles.input,
                        {
                            [styles.withCopyButton]: showCopyButton,
                        },
                        className,
                    )}
                    {...props}
                    disabled
                />
                {showCopyButton && (
                    <button onClick={handleCopy} type="button" className={styles.copyButton}>
                        <motion.div key={String(copied)} animate={{ scale: 1 }} initial={{ scale: 0 }}>
                            {copied ? (
                                <CheckIcon strokeWidth={2.5} className={styles.successIcon} />
                            ) : (
                                <CopyContentIcon />
                            )}
                        </motion.div>
                    </button>
                )}
            </div>
        );
    },
);
